import React, { useEffect } from 'react'
import { History } from 'history'
import { withFeatures } from 'features'
import { match as RouteMatch } from 'react-router-dom'
import { usePermission } from 'admin/hocs'
import { ActionButton } from 'uiComponents/buttons/'
import { FormItem, FormItemName } from 'uiComponents/form/formElements'
import { SingleSelect, SingleSelectOption } from 'uiComponents/input'
import { Column, ColumnGap, ButtonWrapper, TextWithHorizontalLine } from 'uiComponents/pageElements'
import { Row, Col } from 'uiComponents/flex'
import ExternalConfigurationSection from './externalConfigurations'
import { Price } from 'products/utils'
import CalendarBlock from 'products/components/calendarBlock'
import Infotip from 'uiComponents/infotip'
import { ProductListWithPath } from 'products/crud/common'
import TextInputFieldFormik from 'uiComponents/input/textInput/textInputFieldFormik'
import DatePickerFieldFormik from 'uiComponents/input/date/datePickerFieldFormik'
import { useField } from 'formik'
import { showSuccessMessage } from 'uiComponents/message'
import { useTranslation } from 'translations'

interface DetailsFormParams {
    accountSlug: string
    id: string
}

interface DetailsFormProps {
    history: History
    match: RouteMatch<DetailsFormParams>
    hasFeature: (feature: string, slug: string) => boolean
    accountSlug: string
    onMonthChange: (month: Date) => void
    calendarData: Price[] | null
    showCalendar: boolean
    categoryIds: string[] | null
    now: Date
    calendarLoading: boolean
    viewMode: 'flat' | 'nested' | ''
    allCategories: ProductListWithPath[]
    selectedCat: string
    onCategoryChange: (cat: string) => void
}

function DetailsForm({
    accountSlug,
    history,
    onMonthChange,
    calendarData,
    categoryIds,
    match,
    calendarLoading,
    showCalendar,
    allCategories,
    selectedCat,
    onCategoryChange,
    viewMode,
    now,
}: DetailsFormProps) {
    const [validFromField] = useField('validFrom')
    const [validToField] = useField('validTo')
    const [showFromField] = useField('showFrom')
    const [showToField] = useField('showTo')
    const { hasPermission } = usePermission()
    const { t } = useTranslation()

    useEffect(() => {
        const displayInfoMessage = async () => {
            showSuccessMessage(
                t('products.validity.form.calendarInfoMessage'),
                'calendar_info',
            )
        }
        if (
            !window.localStorage.getItem('CalendarExplanationShown') ||
            window.localStorage.getItem('CalendarExplanationShown') === 'false'
        ) {
            displayInfoMessage()
            window.localStorage.setItem('CalendarExplanationShown', 'true')
        }
    }, [])

    const getCategoryOptions = (): SingleSelectOption[] => {
        const options = categoryIds
            ? categoryIds.map((id) => {
                  return {
                      value: id,
                      name: allCategories.find((cat) => cat.uuid === id)?.name || '',
                  }
              })
            : []
        return options
    }

    return (
        <>
            <ExternalConfigurationSection
                history={history}
                accountSlug={accountSlug}
                id={match.params.id}
                viewMode={viewMode}
            />
            <Row>
                <Column style={{ zIndex: 11 }}>
                    <Row>
                        <Col span={12}>
                            <FormItem htmlFor="productName">
                                <FormItemName>{t('products.validity.form.productName')}</FormItemName>
                                <TextInputFieldFormik name="name" block={true} disabled />
                            </FormItem>
                        </Col>
                    </Row>
                    <Row>
                        <Col span={6}>
                            <FormItem htmlFor="validityStartDate">
                                <FormItemName>{t('products.validity.form.validFrom')}</FormItemName>
                                <DatePickerFieldFormik
                                    id="validityStartDate"
                                    name="validFrom"
                                    upperBoundary={validToField.value ? new Date(validToField.value) : null}
                                />
                            </FormItem>
                        </Col>
                        <Col span={6}>
                            <FormItem htmlFor="validityEndDate">
                                <FormItemName>
                                    {t('products.validity.form.validTo')}
                                    <Infotip pointer="left">{t('products.validity.form.dateInclusive')}</Infotip>
                                </FormItemName>
                                <DatePickerFieldFormik
                                    id="validityEndDate"
                                    name="validTo"
                                    lowerBoundary={validFromField.value ? new Date(validFromField.value) : null}
                                />
                            </FormItem>
                        </Col>
                    </Row>
                    <Row>
                        <Col span={6}>
                            <FormItem htmlFor="visibilityStartDate">
                                <FormItemName>{t('products.validity.form.showFrom')}</FormItemName>
                                <DatePickerFieldFormik
                                    id="visibilityStartDate"
                                    name="showFrom"
                                    upperBoundary={showToField.value ? new Date(showToField.value) : null}
                                />
                            </FormItem>
                        </Col>
                        <Col span={6}>
                            <FormItem htmlFor="visibilityEndDate">
                                <FormItemName>
                                    {t('products.validity.form.showTo')}
                                    <Infotip>{t('products.validity.form.dateInclusive')}</Infotip>
                                </FormItemName>
                                <DatePickerFieldFormik
                                    id="visibilityEndDate"
                                    name="showTo"
                                    lowerBoundary={showFromField.value ? new Date(showFromField.value) : null}
                                />
                            </FormItem>
                        </Col>
                    </Row>
                </Column>
                <ColumnGap />
                <Column>
                    {showCalendar && (
                        <>
                            <TextWithHorizontalLine
                                text={t('products.validity.form.calendarPreview')}
                                alignment="left"
                                style={{ marginTop: '1em', marginBottom: '1.7em' }}
                            />
                            {!!categoryIds && categoryIds.length > 0 && (
                                <SingleSelect
                                    id="cat-select"
                                    options={getCategoryOptions()}
                                    selected={selectedCat}
                                    height="2.5rem"
                                    maxHeight="11em"
                                    noSelectOption={t('products.validity.form.selectCategory')}
                                    onSelect={onCategoryChange}
                                    style={{ marginBottom: '0.625em', zIndex: 11 }}
                                />
                            )}
                            <CalendarBlock
                                calendarData={calendarData}
                                loading={calendarLoading}
                                now={now}
                                onMonthChange={onMonthChange}
                            />
                        </>
                    )}
                </Column>
            </Row>
            <ButtonWrapper>
                <ActionButton
                    size="large"
                    secondary
                    onClick={() => {
                        history.push(`/account/${accountSlug}/products/home/${viewMode}`)
                    }}
                >
                    {hasPermission('edit_product_validity', accountSlug) ? t('common.cancel') : t('common.back')}
                </ActionButton>
                {hasPermission('edit_product_validity', accountSlug) && (
                    <ActionButton id="saveSettings" type="submit" size="large" style={{ marginLeft: '1.5em' }}>
                        {t('common.save')}
                    </ActionButton>
                )}
            </ButtonWrapper>
        </>
    )
}

export default withFeatures(DetailsForm)
